import React, { useState, useRef } from 'react';
import { Copy, Check, ChevronDown, Terminal, Package, BookOpen, Zap, Globe, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';

interface ExternalTrackerSnippetProps {
  storeId: string;
  accountId?: string;
  funnelId: string;
  stepId: string;
  stepName?: string;
  stepType?: string;
  externalUrl?: string;
}

type TabKey = 'cdn' | 'npm' | 'api';

export function ExternalTrackerSnippet({
  storeId,
  accountId,
  funnelId,
  stepId,
  stepName,
}: ExternalTrackerSnippetProps) {
  const { t } = useTranslation('storefront');
  const [activeTab, setActiveTab] = useState<TabKey>('cdn');
  const [copied, setCopied] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const codeRef = useRef<HTMLPreElement>(null);

  const cdnScript = `<script src="https://cdn.jsdelivr.net/npm/@tagadapay/plugin-sdk/dist/external-tracker.min.js"></script>
<script>
  TagadaTracker.init({
    storeId: '${storeId}',
    accountId: '${accountId || 'acc_xxx'}',
    funnelId: '${funnelId}',
    stepId: '${stepId}',
    stepName: '${stepName || 'My Page'}',
  });
</script>`;

  const npmScript = `import { TagadaTracker } from '@tagadapay/plugin-sdk/v2/standalone';

TagadaTracker.init({
  storeId: '${storeId}',
  accountId: '${accountId || 'acc_xxx'}',
  funnelId: '${funnelId}',
  stepId: '${stepId}',
  stepName: '${stepName || 'My Page'}',
});`;

  const apiReference = `// Navigate to next funnel step
await TagadaTracker.navigate();

// Navigate with custom event data
await TagadaTracker.navigate({
  eventType: 'form.submitted',
  eventData: { email: 'user@example.com' },
});

// Track a custom event
await TagadaTracker.trackEvent({
  name: 'video_play',
  data: { videoId: 'hero-video' },
});

// Get session info
const session = TagadaTracker.getSession();
const customerId = TagadaTracker.getCustomerId();

// Build URL with funnel context
const url = TagadaTracker.buildUrl('https://example.com/next');

// SPA: reset for a new step
await TagadaTracker.reset('step_newId');`;

  const currentCode = activeTab === 'cdn' ? cdnScript : activeTab === 'npm' ? npmScript : apiReference;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(currentCode);
      setCopied(true);
      toast.success(t('externalTracker.copiedToClipboard'));
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error(t('externalTracker.failedToCopy'));
    }
  };

  const tabs: { key: TabKey; label: string; icon: React.ReactNode }[] = [
    { key: 'cdn', label: t('externalTracker.scriptTag'), icon: <Terminal className="h-3 w-3" /> },
    { key: 'npm', label: t('externalTracker.npmPackage'), icon: <Package className="h-3 w-3" /> },
    { key: 'api', label: t('externalTracker.apiReference'), icon: <BookOpen className="h-3 w-3" /> },
  ];

  const features = [
    { icon: <Zap className="h-3 w-3" />, text: t('externalTracker.featureAutoTrack') },
    { icon: <Globe className="h-3 w-3" />, text: t('externalTracker.featureCaptureDevice') },
    { icon: <ArrowRight className="h-3 w-3" />, text: t('externalTracker.featurePreserveIdentity') },
  ];

  return (
    <div className="border-t border-slate-200 dark:border-slate-700">
      {/* Header */}
      <div className="px-4 pt-4 pb-3">
        <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
          {t('externalTracker.trackingSetup')}
        </p>
        <p className="mt-1 text-xs text-slate-400 dark:text-slate-500">
          {t('externalTracker.addSnippetDescription')}
        </p>
      </div>

      {/* Feature pills */}
      <div className="flex flex-wrap gap-1.5 px-4 pb-3">
        {features.map((f, i) => (
          <span
            key={i}
            className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-600 dark:bg-slate-800 dark:text-slate-400"
          >
            {f.icon}
            {f.text}
          </span>
        ))}
      </div>

      {/* Tab selector */}
      <div className="flex items-center gap-0.5 border-b border-slate-200 px-4 dark:border-slate-700">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={cn(
              'inline-flex items-center gap-1.5 border-b-2 px-3 py-2 text-xs font-medium transition-colors',
              activeTab === tab.key
                ? 'border-primary text-primary'
                : 'border-transparent text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-300'
            )}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      {/* Code block */}
      <div className="relative">
        <pre
          ref={codeRef}
          className="max-h-[280px] overflow-auto bg-slate-950 px-4 py-3 font-mono text-[11px] leading-relaxed text-slate-300"
        >
          <code>{currentCode}</code>
        </pre>
        <Button
          size="icon"
          variant="ghost"
          className="absolute right-2 top-2 h-7 w-7 bg-slate-800 text-slate-400 hover:bg-slate-700 hover:text-white"
          onClick={handleCopy}
        >
          {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
        </Button>
      </div>

      {/* Hint */}
      {activeTab === 'cdn' && (
        <p className="px-4 py-2 text-[11px] text-slate-400 dark:text-slate-500">
          {t('externalTracker.pasteBeforeBody')} <code className="rounded bg-slate-100 px-1 font-mono text-[10px] dark:bg-slate-800">&lt;/body&gt;</code>
        </p>
      )}
      {activeTab === 'npm' && (
        <p className="px-4 py-2 text-[11px] text-slate-400 dark:text-slate-500">
          {t('externalTracker.installWith')} <code className="rounded bg-slate-100 px-1 font-mono text-[10px] dark:bg-slate-800">npm i @tagadapay/plugin-sdk</code>
        </p>
      )}

      {/* URL Parameters — collapsible */}
      <div className="border-t border-slate-200 dark:border-slate-700">
        <button
          onClick={() => setShowAdvanced(!showAdvanced)}
          className="flex w-full items-center justify-between px-4 py-2.5 text-left transition-colors hover:bg-slate-50 dark:hover:bg-slate-800/50"
        >
          <span className="text-xs font-medium text-slate-600 dark:text-slate-400">
            {t('externalTracker.urlParams')}
          </span>
          <ChevronDown
            className={cn(
              'h-3.5 w-3.5 text-slate-400 transition-transform',
              showAdvanced && 'rotate-180'
            )}
          />
        </button>
        {showAdvanced && (
          <div className="border-t border-slate-100 px-4 pb-4 pt-2 dark:border-slate-800">
            <p className="text-[11px] leading-relaxed text-slate-500 dark:text-slate-400">
              {t('externalTracker.urlParamsDescription')}
            </p>
            <code className="mt-2 block rounded-md bg-slate-100 p-2 font-mono text-[10px] text-slate-600 dark:bg-slate-800 dark:text-slate-400">
              ?token=...&funnelSessionId=...&funnelId={funnelId}&storeId={storeId}
            </code>
            <p className="mt-2 text-[10px] text-slate-400 dark:text-slate-500">
              {t('externalTracker.noExtraWorkNeeded')}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export default ExternalTrackerSnippet;
